// src/context/CatalogosContext.js
// Caché en memoria + localStorage de catálogos que casi no cambian:
//   bodegas, empresas, redes_sociales, departamentos, productos, municipios, usuarios
// Estrategia:
//   1. Al montar, intenta cargar de localStorage si está fresco (< 30 min)
//   2. Si no hay cache o expiró, lee de Firestore y guarda en localStorage
//   3. Cuando se crea/edita un catálogo, las funciones refrescarX() actualizan
//      Context y localStorage al instante (la sesión que hizo el cambio ve los datos
//      actualizados; otros usuarios los ven cuando expire SU localStorage o refresquen).
//   4. PRODUCTOS "POR CAMBIOS" (13/10/2026 — Toto: "1 y 2"; docs/planes/COSTOS_GOOGLE_CLOUD.md §6.5): con el
//      interruptor `configuracion/catalogo_productos.porCambios` prendido, al abrir la pestaña NO se vuelven a bajar
//      los ~4,000 productos: se pregunta cuáles cambiaron desde la última vez (marca `catalogoCambioEn`, que pone el
//      servidor en cada cambio: functions/catalogos.js) y se bajan solo esos. La lista completa se baja igual una vez
//      al día (red de seguridad) y si algo falla al preguntar. Apagado, todo queda como en los puntos 1-3.
//      La parte pura, con pruebas: ./catalogoProductosCambios.js. Las listas chicas siguen como en los puntos 1-3.

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { collection, getDocs, getDoc, doc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';
import {
  CAMPO_MARCA, sinMarca, marcaMasNueva, ordenarPorNombre, aplicarCambios, decidirCarga, desdeCuando, mismaMeta
} from './catalogoProductosCambios';

const CatalogosContext = createContext();

// ── Configuración del cache localStorage ──
const STORAGE_KEY = 'paralellum_catalogos_v1';
const TTL_MS = 30 * 60 * 1000; // 30 minutos

// Helpers para leer/escribir cache
// La caché entera, tenga la edad que tenga: { timestamp, data, productosMeta }. `productosMeta` (13/10/2026) =
// { completaEn: cuándo se bajó la lista COMPLETA de productos (hora de esta computadora), marca: la marca
// `catalogoCambioEn` más nueva que se vio (hora del servidor, o null) }. La versión anterior del ERP no la escribe:
// una caché sin ella hace bajar la lista completa.
const leerCacheCrudo = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && parsed.data && typeof parsed.data === 'object' ? parsed : null;
  } catch (err) {
    console.warn('Error leyendo cache de catálogos:', err);
    return null;
  }
};

// `timestamp` es la edad de las listas chicas (y de la regla de los 30 minutos). Por defecto, ahora.
const guardarCacheLocalStorage = (data, { productosMeta = null, timestamp = Date.now() } = {}) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      timestamp,
      data,
      ...(productosMeta ? { productosMeta } : {})
    }));
  } catch (err) {
    // localStorage puede estar lleno o deshabilitado — silencioso
    console.warn('Error guardando cache de catálogos:', err);
  }
};

const actualizarClaveEnCache = (clave, valor, { productosMeta = null } = {}) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    parsed.data = { ...parsed.data, [clave]: valor };
    parsed.timestamp = Date.now(); // Resetear TTL al refrescar una clave
    if (productosMeta) parsed.productosMeta = productosMeta;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch (err) {
    console.warn('Error actualizando cache de catálogos:', err);
  }
};

// Las 9 listas chicas (todas menos productos): se cargan y se guardan juntas, como siempre.
const CLAVES_CHICAS = ['bodegas', 'empresas', 'redesSociales', 'departamentos', 'departamentosEmpresa',
  'municipios', 'usuarios', 'complementos', 'setsProductos'];
const tieneLasChicas = (data) => !!data && CLAVES_CHICAS.every((k) => data[k]);
const soloLasChicas = (data) => Object.fromEntries(CLAVES_CHICAS.map((k) => [k, data[k]]));

// 🔴 Interruptor de la lista de productos "por cambios" (1 lectura al abrir la pestaña). Apagado, sin el documento o si
// la lectura falla: como antes del 13/10/2026. Se prende y se apaga desde un script (docs/planes/COSTOS_GOOGLE_CLOUD.md §6.5).
const porCambiosPrendido = async () => {
  try {
    const snap = await getDoc(doc(db, 'configuracion', 'catalogo_productos'));
    return !!(snap.exists() && snap.data().porCambios === true);
  } catch (err) {
    return false;
  }
};

// Solo en `npm start`: qué hizo la lista de productos al abrir la pestaña (para probarlo).
const avisoDesarrollo = (texto) => {
  if (process.env.NODE_ENV === 'development') console.info(`[Catálogo] ${texto}`);
};

export function useCatalogos() {
  return useContext(CatalogosContext);
}

export function CatalogosProvider({ children }) {
  const { user } = useAuth();

  const [bodegas, setBodegas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [redesSociales, setRedesSociales] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [departamentosEmpresa, setDepartamentosEmpresa] = useState([]);
  const [productos, setProductos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  // Reglas de productos complemento (el estuche que va con los lentes). Se leen acá
  // porque las necesita CADA pedido que se crea: sin caché serían lecturas repetidas
  // por pedido. Son pocas decenas de documentos. Ver `src/utils/complementos.js`.
  const [complementos, setComplementos] = useState([]);
  // SETS DE PRODUCTOS (lo que la tienda vende como uno y el ERP arma con piezas). Mismo motivo:
  // los necesita cada pedido de Shopify y el Crear Pedido manual. Pocos documentos (activos e
  // inactivos: el consumidor filtra). Ver `src/utils/sets.js` y CALCULOS.md → "Sets de productos".
  const [setsProductos, setSetsProductos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [cargado, setCargado] = useState(false);

  // ── Carga individual de cada catálogo (con sus filtros y orden originales) ──
  const cargarBodegas = useCallback(async () => {
    const snap = await getDocs(collection(db, 'bodegas'));
    const arr = [];
    snap.forEach(doc => {
      const data = doc.data();
      if (data.activa && !data.nombre.toUpperCase().includes('BASURERO')) {
        arr.push({ id: doc.id, ...data });
      }
    });
    return arr.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base', numeric: true }));
  }, []);

  const cargarEmpresas = useCallback(async () => {
    const snap = await getDocs(collection(db, 'empresas'));
    const arr = [];
    snap.forEach(doc => {
      const data = doc.data();
      if (data.activa) arr.push({ id: doc.id, ...data });
    });
    return arr.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base', numeric: true }));
  }, []);

  const cargarRedesSociales = useCallback(async () => {
    const snap = await getDocs(collection(db, 'redes_sociales'));
    const arr = [];
    snap.forEach(doc => {
      const data = doc.data();
      if (data.activa) arr.push({ id: doc.id, ...data });
    });
    return arr.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base', numeric: true }));
  }, []);

  const cargarDepartamentos = useCallback(async () => {
    const snap = await getDocs(collection(db, 'departamentos'));
    const arr = [];
    snap.forEach(doc => {
      arr.push({ id: doc.id, ...doc.data() });
    });
    return arr.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base', numeric: true }));
  }, []);

  // Departamentos de la EMPRESA (áreas internas: SAC, Mensajería, Producción...).
  // NO confundir con `departamentos` (geográficos de Guatemala).
  // Se traen TODOS (activos e inactivos) — el consumidor filtra por `activo`
  // (regla: el cache incluye todo, el filtro va en quien lo usa).
  const cargarDepartamentosEmpresa = useCallback(async () => {
    const snap = await getDocs(collection(db, 'departamentos_empresa'));
    const arr = [];
    snap.forEach(doc => {
      arr.push({ id: doc.id, ...doc.data() });
    });
    return arr.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base', numeric: true }));
  }, []);

  // Lista COMPLETA de productos (la de siempre: solo los activos, por nombre) + los datos para después preguntar solo
  // por los cambios: cuándo se bajó y la marca más nueva que había (de todos, también de los inactivos). Los productos
  // se guardan SIN la marca (sinMarca): las pantallas los reciben con los mismos campos que antes.
  const cargarProductosCompleta = useCallback(async () => {
    const snap = await getDocs(collection(db, 'productos'));
    const arr = [];
    const todos = [];
    snap.forEach(d => {
      const data = d.data();
      todos.push(data);
      if (data.activo) arr.push({ id: d.id, ...sinMarca(data) });
    });
    return { lista: ordenarPorNombre(arr), meta: { completaEn: Date.now(), marca: marcaMasNueva(todos) } };
  }, []);

  // Solo los productos que cambiaron desde la marca más nueva vista (casi siempre 0 a 10 lecturas), juntados con la
  // lista guardada. `cambio` dice si la lista de verdad cambió.
  const traerCambiosProductos = useCallback(async (lista, meta) => {
    const snap = await getDocs(query(
      collection(db, 'productos'),
      where(CAMPO_MARCA, '>=', Timestamp.fromMillis(desdeCuando(meta.marca)))
    ));
    const crudos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const r = aplicarCambios(lista, crudos.map(sinMarca));
    return { ...r, llegados: crudos.length, meta: { completaEn: meta.completaEn, marca: marcaMasNueva(crudos, meta.marca) } };
  }, []);

  const cargarMunicipios = useCallback(async () => {
    const snap = await getDocs(collection(db, 'municipios'));
    const arr = [];
    snap.forEach(doc => {
      arr.push({ id: doc.id, ...doc.data() });
    });
    return arr;
  }, []);

  const cargarUsuarios = useCallback(async () => {
    const snap = await getDocs(collection(db, 'usuarios'));
    const arr = [];
    snap.forEach(doc => {
      arr.push({ id: doc.id, ...doc.data() });
    });
    return arr;
  }, []);

  // Reglas de complemento (id del doc = id del producto principal). Se traen todas:
  // son pocas y el consumidor filtra las activas (regla del proyecto: cachear completo,
  // filtrar en el consumidor).
  const cargarComplementos = useCallback(async () => {
    const snap = await getDocs(collection(db, 'complementos_producto'));
    const arr = [];
    snap.forEach(doc => {
      arr.push({ id: doc.id, ...doc.data() });
    });
    return arr;
  }, []);

  // Sets de productos: todos (activos e inactivos), ordenados por el nombre de la tienda.
  const cargarSetsProductos = useCallback(async () => {
    const snap = await getDocs(collection(db, 'sets_productos'));
    const arr = [];
    snap.forEach(doc => {
      arr.push({ id: doc.id, ...doc.data() });
    });
    return arr.sort((a, b) => (a.nombreTienda || '').localeCompare(b.nombreTienda || '', 'es', { sensitivity: 'base', numeric: true }));
  }, []);

  // Las 9 listas chicas de Firestore, juntas (como siempre).
  const cargarChicas = useCallback(async () => {
    const [bod, emp, red, dep, depEmp, mun, usr, comp, setsP] = await Promise.all([
      cargarBodegas(),
      cargarEmpresas(),
      cargarRedesSociales(),
      cargarDepartamentos(),
      cargarDepartamentosEmpresa(),
      cargarMunicipios(),
      cargarUsuarios(),
      cargarComplementos(),
      cargarSetsProductos()
    ]);
    return {
      bodegas: bod, empresas: emp, redesSociales: red, departamentos: dep, departamentosEmpresa: depEmp,
      municipios: mun, usuarios: usr, complementos: comp, setsProductos: setsP
    };
  }, [cargarBodegas, cargarEmpresas, cargarRedesSociales, cargarDepartamentos, cargarDepartamentosEmpresa, cargarMunicipios, cargarUsuarios, cargarComplementos, cargarSetsProductos]);

  const ponerChicas = useCallback((c) => {
    setBodegas(c.bodegas);
    setEmpresas(c.empresas);
    setRedesSociales(c.redesSociales);
    setDepartamentos(c.departamentos);
    setDepartamentosEmpresa(c.departamentosEmpresa);
    setMunicipios(c.municipios);
    setUsuarios(c.usuarios);
    setComplementos(c.complementos);
    setSetsProductos(c.setsProductos);
  }, []);

  // ── Carga inicial: localStorage primero (si está fresco), si no Firestore ──
  useEffect(() => {
    if (!user || cargado || cargando) return;

    const crudo = leerCacheCrudo();
    const ahora = Date.now();
    const data = crudo ? crudo.data : null;
    // La regla de siempre: lo guardado hace menos de 30 minutos se usa sin preguntar a Firestore.
    const fresca = !!data && (ahora - (crudo.timestamp || 0)) <= TTL_MS;
    const chicasFrescas = fresca && tieneLasChicas(data);
    const productosGuardados = data && Array.isArray(data.productos) ? data.productos : null;
    const productosFrescos = fresca && !!productosGuardados;
    const metaGuardada = crudo && crudo.productosMeta ? crudo.productosMeta : null;

    // Lo que la regla de siempre deja usar, se muestra YA: las pantallas no esperan a la red.
    if (chicasFrescas) ponerChicas(data);
    if (productosFrescos) setProductos(productosGuardados);

    setCargando(true);
    const tareaChicas = chicasFrescas ? Promise.resolve(null) : cargarChicas();
    const tareaProductos = (async () => {
      // El interruptor solo se mira si hay una lista guardada por esta versión del ERP (con sus datos).
      const prendido = !!(productosGuardados && metaGuardada) && await porCambiosPrendido();
      if (prendido && decidirCarga(productosGuardados, metaGuardada, ahora) === 'cambios') {
        // "Por cambios": solo los productos que cambiaron. Si falla, la lista completa.
        try {
          const r = await traerCambiosProductos(productosGuardados, metaGuardada);
          avisoDesarrollo(`productos por cambios: llegaron ${r.llegados}, ${r.cambio ? 'la lista cambió' : 'sin cambios'}.`);
          return r;
        } catch (err) {
          console.warn('Catálogo: no se pudo preguntar qué productos cambiaron; se baja la lista completa.', err);
        }
      } else if (!prendido && productosFrescos) {
        // La regla de siempre: la lista guardada hace menos de 30 minutos, sin preguntar.
        avisoDesarrollo('productos: la lista guardada de menos de 30 minutos, sin preguntar.');
        return { lista: productosGuardados, cambio: false, meta: metaGuardada };
      }
      // Lista completa: sin lista guardada o vencida (interruptor apagado), la red de seguridad del día (prendido), o
      // porque falló la pregunta por cambios.
      const r = await cargarProductosCompleta();
      avisoDesarrollo(`productos: lista completa (${r.lista.length} activos).`);
      return { ...r, cambio: true };
    })();

    Promise.all([tareaChicas, tareaProductos])
      .then(([chicas, prod]) => {
        if (chicas) ponerChicas(chicas);
        if (prod.cambio || !productosFrescos) setProductos(prod.lista);
        setCargado(true);
        // Guardar en localStorage para próximas sesiones/refreshes/pestañas, solo si algo cambió (la caché de
        // productos pesa varios MB: reescribirla sin motivo en cada pestaña no tiene sentido).
        const metaCambio = prod.meta !== metaGuardada && !mismaMeta(prod.meta, metaGuardada);
        if (chicas || prod.cambio || metaCambio) {
          guardarCacheLocalStorage(
            { ...(chicas || soloLasChicas(data)), productos: prod.lista },
            // Las listas chicas que no se volvieron a bajar conservan su edad (la regla de los 30 minutos).
            { productosMeta: prod.meta, timestamp: chicas ? Date.now() : crudo.timestamp }
          );
        }
      })
      .catch(err => console.error('Error cargando catálogos:', err))
      .finally(() => setCargando(false));
  }, [user, cargado, cargando, cargarChicas, cargarProductosCompleta, traerCambiosProductos, ponerChicas]);

  // ── Funciones para refrescar a demanda (cuando se crea/edita un catálogo) ──
  // Cada una actualiza Context Y localStorage para que el usuario que hizo el cambio
  // lo vea al instante incluso al cambiar de pestaña/refrescar.
  // Lista completa (no "por cambios"): se llama justo después de guardar un producto, y su marca la pone el servidor
  // segundos después; preguntar por cambios en ese momento podría no traerlo todavía.
  const refrescarProductos = useCallback(async () => {
    const { lista, meta } = await cargarProductosCompleta();
    setProductos(lista);
    actualizarClaveEnCache('productos', lista, { productosMeta: meta });
  }, [cargarProductosCompleta]);

  const refrescarBodegas = useCallback(async () => {
    const arr = await cargarBodegas();
    setBodegas(arr);
    actualizarClaveEnCache('bodegas', arr);
  }, [cargarBodegas]);

  const refrescarEmpresas = useCallback(async () => {
    const arr = await cargarEmpresas();
    setEmpresas(arr);
    actualizarClaveEnCache('empresas', arr);
  }, [cargarEmpresas]);

  const refrescarRedesSociales = useCallback(async () => {
    const arr = await cargarRedesSociales();
    setRedesSociales(arr);
    actualizarClaveEnCache('redesSociales', arr);
  }, [cargarRedesSociales]);

  const refrescarMunicipios = useCallback(async () => {
    const arr = await cargarMunicipios();
    setMunicipios(arr);
    actualizarClaveEnCache('municipios', arr);
  }, [cargarMunicipios]);

  const refrescarDepartamentos = useCallback(async () => {
    const arr = await cargarDepartamentos();
    setDepartamentos(arr);
    actualizarClaveEnCache('departamentos', arr);
  }, [cargarDepartamentos]);

  const refrescarDepartamentosEmpresa = useCallback(async () => {
    const arr = await cargarDepartamentosEmpresa();
    setDepartamentosEmpresa(arr);
    actualizarClaveEnCache('departamentosEmpresa', arr);
  }, [cargarDepartamentosEmpresa]);

  const refrescarUsuarios = useCallback(async () => {
    const arr = await cargarUsuarios();
    setUsuarios(arr);
    actualizarClaveEnCache('usuarios', arr);
  }, [cargarUsuarios]);

  const refrescarComplementos = useCallback(async () => {
    const arr = await cargarComplementos();
    setComplementos(arr);
    actualizarClaveEnCache('complementos', arr);
  }, [cargarComplementos]);

  // Devuelve la lista nueva (05/10/2026): el armado de un pedido la necesita en el mismo momento,
  // sin esperar a que React actualice el estado (ver CrearPedidoShopify → completarCatalogosFaltantes).
  const refrescarSetsProductos = useCallback(async () => {
    const arr = await cargarSetsProductos();
    setSetsProductos(arr);
    actualizarClaveEnCache('setsProductos', arr);
    return arr;
  }, [cargarSetsProductos]);

  const valor = {
    bodegas,
    empresas,
    redesSociales,
    departamentos,
    departamentosEmpresa,
    productos,
    municipios,
    usuarios,
    complementos,
    setsProductos,
    cargandoCatalogos: cargando,
    catalogosListos: cargado,
    refrescarProductos,
    refrescarBodegas,
    refrescarEmpresas,
    refrescarRedesSociales,
    refrescarMunicipios,
    refrescarDepartamentos,
    refrescarDepartamentosEmpresa,
    refrescarUsuarios,
    refrescarComplementos,
    refrescarSetsProductos
  };

  return (
    <CatalogosContext.Provider value={valor}>
      {children}
    </CatalogosContext.Provider>
  );
}
